import { KeyboardEvent, useEffect, useMemo } from 'react';
import { ComparableListings } from 'src/components/Listings/ComparableListings';
import { useActiveFocusContext } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { getAllInPriceFromListPrice } from 'src/utils/inventoryUtils';
import {
  ActivityLogClient,
  CompListing,
  Listing,
  ListingPriceCalculation,
  Marketplace,
} from 'src/WebApiController';

import * as styles from './ConnectedComparableListings.css';
import { useSellerListings } from './ConnectedComparableListingsV2.hooks';

export type ListingWithSeatScore = Listing & {
  seatScore?: number | null | undefined;
};

export type CompListingWithSeatScore = CompListing & {
  seatScore?: number | null | undefined;
};

export type ConnectedComparableListingsV2Props = {
  isLoading?: boolean;
  priceOverride?: number | null;
  compListings: CompListingWithSeatScore[];
  seatScore?: number | null;
  sortBy?: 'price' | 'dealScore';
  showAutoPriceResults?: boolean;
  isExcludingFanInventory?: boolean;
  isSortDescending?: boolean;
  listingPriceCalc?: ListingPriceCalculation | null;
  listings: Listing[]; // Seller listings
  selectedListingId?: number; // Your selected listing id
};

export const ConnectedComparableListingsV2 = ({
  isLoading,
  compListings,
  listings,
  selectedListingId = 0,
  listingPriceCalc,
  isExcludingFanInventory,
  ...rest
}: ConnectedComparableListingsV2Props) => {
  const { activeAccountWebClientConfig, loginContext } = useAppContext();

  const { onKeyDownHandler, activeItem } = useActiveFocusContext();
  const activeItemIndex = activeItem?.index;

  const onCurrentKeyDownHandler = (e: KeyboardEvent) => {
    onKeyDownHandler(e, activeItemIndex ?? 0);
  };

  const { sellerListings, comparableListings, currentListing } =
    useSellerListings(
      selectedListingId,
      listings,
      compListings,
      listingPriceCalc
    );

  const stubHubMkpSettings =
    loginContext?.user?.activeAccount?.marketplaceSettings?.find(
      (m) => m.mkp === Marketplace.StubHub
    );

  const comparableListingsLoaded = useMemo(() => {
    return !isLoading && currentListing?.id != null;
  }, [currentListing?.id, isLoading]);

  useEffect(() => {
    if (comparableListingsLoaded && currentListing) {
      new ActivityLogClient(activeAccountWebClientConfig).trackListingCompsView(
        currentListing.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableListingsLoaded]);

  const hasBetterPrice = useMemo(() => {
    const newPrcInclFanInv = listingPriceCalc?.newPrcInclFanInv;
    const newPrcExclFanInv = listingPriceCalc?.websitePrice?.amt;

    if (!newPrcInclFanInv || !newPrcExclFanInv) {
      return false;
    }
    return newPrcInclFanInv < newPrcExclFanInv;
  }, [listingPriceCalc]);

  return (
    <Stack
      className={styles.root}
      direction="column"
      gap="l"
      onKeyDown={onCurrentKeyDownHandler}
    >
      {isExcludingFanInventory && hasBetterPrice && (
        <div className={styles.fanInventoryInfoContainer}>
          <InfoOutlineIcon
            fill={vars.color.textWarning}
            size={vars.iconSize.l}
          />
          <span className={styles.statementText}>
            <Content id={ContentId.NonCompetitiveListing} />
          </span>
        </div>
      )}
      {isLoading ? null : (
        <ComparableListings
          currentListings={sellerListings}
          comparableListings={comparableListings}
          floorPrice={
            (listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor)
              ? getAllInPriceFromListPrice(
                  listingPriceCalc?.cmpFlr?.amt ?? currentListing?.procsFloor,
                  currentListing,
                  stubHubMkpSettings?.sellerFee
                )
              : undefined
          }
          ceilingPrice={
            (listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil)
              ? getAllInPriceFromListPrice(
                  listingPriceCalc?.cmpCeil?.amt ?? currentListing?.procsCeil,
                  currentListing,
                  stubHubMkpSettings?.sellerFee
                )
              : undefined
          }
          listingPriceCalc={listingPriceCalc}
          isExcludingFanInventory={isExcludingFanInventory}
          {...rest}
        />
      )}
    </Stack>
  );
};
